/**
 * React Hooks for Portfolio Data
 * Provides easy access to portfolio data with loading states
 */

import { useState, useEffect, useMemo } from 'react';
import * as dataLoader from '../data/dataLoader';

/**
 * Hook for loading the user profile
 */
export function useProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    dataLoader.loadProfile()
      .then(data => {
        setProfile(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  }, []);

  return { profile, loading, error };
}

/**
 * Hook for loading all projects from manifest
 */
export function useProjects() {
  const projects = useMemo(() => dataLoader.getAllProjects(), []);
  return { projects, loading: false };
}

/**
 * Hook for loading a single project by slug
 */
export function useProject(slug) {
  const project = useMemo(() => dataLoader.getProject(slug), [slug]);
  return { project, loading: false };
}

/**
 * Hook for loading featured projects
 */
export function useFeaturedProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.getFeaturedProjects()
      .then(data => {
        setProjects(data);
        setLoading(false);
      });
  }, []);

  return { projects, loading };
}

/**
 * Hook for loading timeline data
 */
export function useTimeline() {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.loadTimeline()
      .then(data => {
        setTimeline(data);
        setLoading(false);
      });
  }, []);

  return { timeline, loading };
}

/**
 * Hook for loading skills data
 */
export function useSkills() {
  const [skills, setSkills] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.loadSkills()
      .then(data => {
        setSkills(data);
        setLoading(false);
      });
  }, []);

  return { skills, loading };
}

/**
 * Hook for loading client logos
 */
export function useClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.loadClients()
      .then(data => {
        setClients(data);
        setLoading(false);
      });
  }, []);

  return { clients, loading };
}

/**
 * Hook for loading tech innovations
 */
export function useInnovations() {
  const [innovations, setInnovations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.loadInnovations()
      .then(data => {
        setInnovations(data);
        setLoading(false);
      });
  }, []);

  return { innovations, loading };
}

/**
 * Hook for loading additional projects
 */
export function useAdditionalProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.loadAdditionalProjects()
      .then(data => {
        setProjects(data);
        setLoading(false);
      });
  }, []);

  return { projects, loading };
}

/**
 * Hook for loading stats
 */
export function useStats() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataLoader.getStats()
      .then(data => {
        setStats(data);
        setLoading(false);
      });
  }, []);

  return { stats, loading };
}
